import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { NestedPropertyPipe } from '@zaoblako/web/shared/pipes/nested-property.pipe';

import { isNil } from 'lodash';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
  AutoCompleteSelectEvent
} from 'primeng/autocomplete';
import {
  FRM_FILTERS_UI_V2_PANEL_CLASS,
  type FiltersUiVersion
} from '@zaoblako/web/core/types/filters-ui-version.type';

@Component({
  standalone: true,
  selector: 'crm-auto-complete',
  templateUrl: './auto-complete.component.html',
  imports: [CommonModule, FormsModule, AutoCompleteModule, NestedPropertyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.frm-filters-ui-v2]': 'filtersUiVersion === "v2"'
  }
})
export class AutoCompleteComponent {
  @ViewChild('autoComplete') public readonly autoComplete: AutoComplete;

  @ContentChild('headerTemplate') public readonly headerTemplate: TemplateRef<any>;
  @ContentChild('itemTemplate') public readonly itemTemplate: TemplateRef<any>;
  @ContentChild('groupTemplate') public readonly groupTemplate: TemplateRef<any>;
  @ContentChild('selectedItemTemplate') public readonly selectedItemTemplate: TemplateRef<any>;
  @ContentChild('footerTemplate') public readonly footerTemplate: TemplateRef<any>;

  public _value: any;
  public _arrayValue: any[];
  public _endValue: any;

  @Input() public set value(value: any | any[]) {
    this.buildValue(value);
  }

  @Output() public valueChange = new EventEmitter<any | any[]>();

  @Output() public completeMethod = new EventEmitter<AutoCompleteCompleteEvent>();

  @Input() public isError = false;
  @Input() public isWithStringValue = false;

  @Input() public optionValue = 'id';
  @Input() public optionLabel = 'name';
  @Input() public class: string;
  @Input() public styleClass: string | string[] | Set<string> | { [klass: string]: any };
  @Input() public disabled = false;
  @Input() public inputStyle: { [p: string]: any } | null | undefined;
  @Input() public inputStyleClass: string;
  @Input() public panelStyleClass: string;
  @Input() public scrollHeight: string;
  @Input() public suggestions: any[];
  @Input() public virtualScroll = false;
  @Input() public virtualScrollItemSize: number;
  @Input() public appendTo: string | null;
  @Input() public placeholder = '';
  @Input() public multiple = false;
  @Input() public dropdown = false;
  @Input() public autofocus = false;
  @Input() public showClear = false;
  @Input() public loading = false;

  /** Оформление как у фильтров FRM 2.0 (`_filters-ui-v2.scss`). */
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  public get mergedPanelStyleClass(): string {
    const base = typeof this.panelStyleClass === 'string' ? this.panelStyleClass.trim() : '';
    const v2 = this.filtersUiVersion === 'v2' ? FRM_FILTERS_UI_V2_PANEL_CLASS : '';
    return [base, v2].filter(Boolean).join(' ');
  }

  /** PrimeNG default; explicit binding must not pass undefined when virtualScroll is on. */
  public get resolvedScrollHeight(): string {
    const height = typeof this.scrollHeight === 'string' ? this.scrollHeight.trim() : '';
    if (!isNil(this.scrollHeight) && height.length > 0) {
      return height;
    }
    return '200px';
  }

  public constructor(private readonly cdRef: ChangeDetectorRef) {}

  public changeValue(event: any | any[]) {
    if (
      (this.isWithStringValue || (typeof event !== 'string' && !this.isWithStringValue)) &&
      event !== this._endValue
    ) {
      this.valueChange.emit(event);
      this._endValue = event;
    }
  }

  public onSelect(event: AutoCompleteSelectEvent) {
    if (event.value !== this._endValue) {
      this.valueChange.emit(event.value);
      this._endValue = event.value;
    }
  }

  public onClear() {
    if (this.showClear && !isNil(this._value)) {
      this.valueChange.emit(null);
      this._endValue = null;
    }
  }

  public onBlur() {
    if (!this.isWithStringValue) {
      setTimeout(() => {
        if (typeof this._value === 'string') {
          if (
            this.suggestions?.length &&
            this.suggestions.findIndex((s) => s[this.optionValue] === this._value) !== -1
          ) {
            this._value = this.suggestions.find((s) => s[this.optionValue] === this._value);
            if (this._endValue !== this._value) {
              this.valueChange.emit(this._value);
              this._endValue = this._value;
            }
          } else {
            this._value = null;
            if (this._endValue !== this._value) {
              this.valueChange.emit(null);
              this._endValue = null;
            }
          }
        }
      }, 250);
    }
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.autoComplete?.inputValue());
  }

  private buildValue(value: any | any[]) {
    if (this.multiple) {
      this._arrayValue = value;
    } else {
      this._value = value;
    }
    this.cdRef.markForCheck();
  }
}
